import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Stack,
} from '@mui/material'
import { useAppDispatch } from '../hooks/redux'
import { useLazyGetMeQuery, useLoginMutation } from '../store/api/authApi'
import { useLazyGetSettingRolesQuery } from '../store/api/settingsApi'
import type { RolePermissionValue } from '../store/api/settingsApi'
import { setCredentials, setPermissions, setUser } from '../store/slices/authSlice'
import type { PermissionsMap } from '../store/slices/authSlice'
import Logo from '../components/Logo'

export default function LoginPage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [login, { isLoading, error }] = useLoginMutation()
  const [getMe] = useLazyGetMeQuery()
  const [getRoles] = useLazyGetSettingRolesQuery()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const result = await login({ username, password }).unwrap()
      dispatch(setCredentials({ token: result.access_token, user: null }))
      try {
        const userData = await getMe().unwrap()
        dispatch(setUser(userData))
        // Load nav permissions from settings/roles for this user's role
        try {
          const rolesData = await getRoles().unwrap()
          const userRole = rolesData.items.find(
            (r) => r.roleCodeUper.toUpperCase() === (userData.role ?? '').toUpperCase()
          )
          if (userRole) {
            const NAV_PERM_KEYS = [
              'purchase', 'procurement', 'saleContract', 'saleControl',
              'factory', 'virtualWarehouse', 'futures', 'position',
              'quality', 'masterData', 'setting', 'factoryBl',
              'factoryNc', 'factoryNs', 'factoryNt', 'factoryPaco', 'matchingPs',
            ] as const
            const perms: PermissionsMap = {}
            for (const key of NAV_PERM_KEYS) {
              const val = userRole[key] as RolePermissionValue
              perms[key] = val === 1 ? 'Edit' : val === 0 ? 'View' : 'NoGrant'
            }
            dispatch(setPermissions(perms))
          }
        } catch {
          // ignore — nav shows all if role lookup fails
        }
      } catch {
        // ignore
      }
      navigate('/home')
    } catch {
      // error from mutation
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'center', md: 'center' },
        justifyContent: 'center',
        gap: { xs: 3, md: 5, lg: 7 },
        bgcolor: '#f8f9fb',
        px: { xs: 2, md: 7 },
        py: { xs: 5, md: 7 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 14% 18%, rgba(201,169,97,0.18) 0, transparent 28%), radial-gradient(circle at 82% 12%, rgba(9,54,51,0.10) 0, transparent 24%), linear-gradient(135deg, #fafbfc 0%, #f8f9fb 52%, #eef0f3 100%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.22,
          backgroundImage:
            'linear-gradient(120deg, rgba(15,23,32,0.035) 1px, transparent 1px), linear-gradient(30deg, rgba(201,169,97,0.06) 1px, transparent 1px)',
          backgroundSize: '54px 54px',
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 980,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.12fr 0.88fr' },
          overflow: 'hidden',
          borderRadius: 5,
          bgcolor: '#ffffff',
          border: '1px solid #e3e6ea',
          boxShadow: '0 28px 80px rgba(15, 23, 32, 0.16)',
        }}
      >
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 520,
            color: '#f8faf9',
            p: { md: 5, lg: 6 },
            background:
              'radial-gradient(circle at 22% 18%, rgba(201,169,97,0.32), transparent 32%), linear-gradient(145deg, #062523 0%, #093633 54%, #0c4744 100%)',
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: '#fbeed1', letterSpacing: 3, fontWeight: 800, fontSize: '0.78rem' }}
          >
            Internal ERP System
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mt: 1.5,
              fontSize: { md: '2.3rem', lg: '2.75rem' },
              lineHeight: 1.08,
              fontWeight: 800,
              letterSpacing: '-0.04em',
            }}
          >
            Tuan Loc Commodities Operations Portal
          </Typography>
          <Typography sx={{ mt: 2.5, maxWidth: 500, color: 'rgba(248,250,249,0.78)', fontSize: '1rem', lineHeight: 1.8 }}>
            Secure access for contract management, procurement, sales control, futures, reporting and internal administration.
          </Typography>
          <Stack direction="row" spacing={1.2} sx={{ mt: 4, flexWrap: 'wrap', rowGap: 1 }}>
            {['Sale Contracts', 'Purchase Contracts', 'Futures Control', 'Factory Control', 'Reports'].map((item) => (
              <Box
                key={item}
                sx={{
                  px: 1.6,
                  py: 0.75,
                  borderRadius: 999,
                  color: '#f8faf9',
                  border: '1px solid rgba(251,238,209,0.28)',
                  bgcolor: 'rgba(255,255,255,0.09)',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}
              >
                {item}
              </Box>
            ))}
          </Stack>
        </Box>
        <Box
          sx={{
            px: { xs: 3, sm: 4, md: 5 },
            py: { xs: 4, md: 5.5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: { xs: 'auto', md: 520 },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.25 }}>
            <Logo variant="login" showText />
          </Box>
          {/* <Typography variant="body2" sx={{ display: 'block', mb: 0.75, color: 'secondary.dark', fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase' }}>
            Employee Access
          </Typography> */}
          <Typography variant="h6" sx={{ display: 'block', mb: 0.5, color: 'primary.dark', fontWeight: 800 }}>
            Sign in to ERP CTRM Platform
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
            Use your company account to continue.
          </Typography>
          {Boolean(error) && (
            <Alert severity="error" sx={{ mb: 2, py: 0.25, textAlign: 'left', fontSize: '0.9rem' }}>
              Invalid username or password.
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              size="medium"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              size="medium"
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                bgcolor: 'primary.dark',
                py: 1.2,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                '&:hover': { bgcolor: 'primary.main' },
              }}
            >
              {isLoading ? 'Processing...' : 'Login'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
